import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ClerkProvider, SignedIn, SignedOut, useAuth, useUser, } from "@clerk/clerk-react";
import { StrictMode, useEffect, useState } from "react";
import SignInPage from "../components/SignInPage/SignInPage";
import { createRoot } from "react-dom/client";
import "../styles/global.css";
import Footer from "../components/Footer/Footer";
import UpdaterForm from "../views/UpdaterForm/UpdaterForm";
import Steps from "../views/Steps/Steps";
import { UserMessageLevels, } from "../views/UpdaterForm/types";
import "../utils/sentry";
import { captureErrorWithContext } from "../utils/sentry";
import * as Sentry from "@sentry/react";
import { buildModelUpdaterPayload, getContext, insertSummarySheet, insertUpdateLog, } from "../services/excel";
import { fetchJson, getOptions, HttpMethod } from "../api/api";
import { generateTimestamp } from "../utils/datetime";
import { responseContainsActualPeriodData } from "../utils/fiscal";
import WatchList from "../views/Watchlist/Watchlist";
// Import your Publishable Key
const PUBLISHABLE_KEY = process.env.CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
    throw new Error("Add your Clerk Publishable Key to the .env file");
}
const CLIENT_URL = process.env.CLIENT_URL;
if (!CLIENT_URL) {
    throw new Error("Add your Client URL to the .env file");
}
function FallbackComponent() {
    return _jsx("div", { children: "An error has occurred" });
}
function SentryUserSync() {
    const { user } = useUser();
    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: user.id,
                email: user.primaryEmailAddress?.emailAddress ?? undefined,
            });
        }
        else {
            // Clear Sentry user on sign out
            Sentry.setUser(null);
        }
    }, [user]);
    return null; // no UI
}
export const App = () => {
    const { getToken } = useAuth();
    const [viewingSteps, setViewingSteps] = useState(false);
    const [viewingForm, setViewingForm] = useState(true);
    const [viewingWatchlist, setViewingWatchlist] = useState(false);
    const [updateIsRunning, setUpdateIsRunning] = useState(false);
    const [steps, setSteps] = useState([]);
    const [multiSelectOptions, setMultiSelectOptions] = useState();
    const [tickers, setTickers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [llmModels, setLlmModels] = useState([]);
    let View;
    (function (View) {
        View["steps"] = "steps";
        View["form"] = "form";
    })(View || (View = {}));
    const [currentView, setCurrentView] = useState();
    const addStep = (data, multiSelectOptions) => {
        setSteps((prev) => [...prev, data]);
        setMultiSelectOptions(multiSelectOptions);
        setViewingSteps(true);
        setViewingForm(false);
        setViewingWatchlist(false);
    };
    const deleteStep = (index) => {
        setSteps((prev) => prev.filter((_, i) => i !== index));
    };
    const runUpdate = async (data, shouldAddSummary) => {
        setUpdateIsRunning(true);
        try {
            return await getContext(async (context) => {
                const worksheet = context.workbook.worksheets.getItem(data.model_sheet);
                const payload = await buildModelUpdaterPayload(context, worksheet, data);
                // This will throw if we fail to build options
                const options = await getOptions({
                    getToken: getToken,
                    method: HttpMethod.post,
                    body: payload,
                });
                const jsonResponse = await fetchJson("/model-update", options);
                if (!jsonResponse.ok) {
                    throw new Error(jsonResponse.error);
                }
                if (jsonResponse.ok && jsonResponse.data?.error_message) {
                    throw new Error(jsonResponse.data.error_message);
                }
                const timestamp = generateTimestamp();
                /**
                 * We will write a summary when the following two conditions are met
                 * 1. The user has elected to write a summary
                 * 2. We are either running a single update or the first step of a multi-step
                 *    update process. When subsequent steps in a multi-step process are run,
                 *    the shouldAddSummary flag should be false
                 */
                if (data.return_summary && shouldAddSummary) {
                    await insertSummarySheet(context, jsonResponse.data, timestamp, data.ticker, data.target_year, data.target_quarter, data.source_urls);
                }
                if (responseContainsActualPeriodData(jsonResponse.data)) {
                    await insertUpdateLog(context, jsonResponse.data, timestamp, worksheet, data.line_items_column, data.comparison_column, data.target_column, data.overwrite_target, data.first_row_number, data.last_row_number);
                    setUpdateIsRunning(false);
                    return {
                        message: "Update Completed",
                        level: UserMessageLevels.success,
                    };
                }
                throw new Error("Response received without actuals data");
            });
        }
        catch (error) {
            setUpdateIsRunning(false);
            let userMessage = "Update failed";
            if (error instanceof OfficeExtension.Error) {
                userMessage = "An issues has occurred with the Excel API";
            }
            else if (error instanceof Error) {
                userMessage = error.message;
            }
            else if (typeof error === "string") {
                userMessage = error;
            }
            captureErrorWithContext(error, {
                extra: { note: "Failed in runUpdate()" },
                tags: { priority: "high" },
            });
            return { message: userMessage, level: UserMessageLevels.error };
        }
    };
    const manageWatchlist = () => {
        if (viewingForm) {
            setCurrentView(View.form);
        }
        if (viewingSteps) {
            setCurrentView(View.steps);
        }
        setViewingWatchlist(true);
        setViewingForm(false);
        setViewingSteps(false);
    };
    const returnToWorkflow = () => {
        setViewingWatchlist(false);
        if (currentView === View.form) {
            setViewingForm(true);
        }
        if (currentView === View.steps) {
            setViewingSteps(true);
        }
    };
    async function fetchCompanies() {
        /**
         * Once per session we will fetch all tickers and store in memory
         * using Fuse. These will then be used for autocomplete suggestions
         */
        let options;
        try {
            options = await getOptions({
                getToken: getToken,
                method: HttpMethod.get,
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: { note: "Failed in fetchCompanies" },
                tags: { priority: "high" },
            });
            return false;
        }
        const response = await fetchJson("/companies", options);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in fetchCompanies",
                },
                tags: {
                    priority: "high",
                },
            });
            return false;
        }
        setCompanies(response.data.companies);
        const tickers = response.data.companies.map(({ name, ticker }) => ({ name, ticker }));
        setTickers(tickers);
    }
    async function fetchLlmModels() {
        /**
         * Once per session we will fetch all LLM models and store in memory
         */
        let options;
        try {
            options = await getOptions({
                getToken: getToken,
                method: HttpMethod.get,
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: { note: "Failed in fetchLlmModels" },
                tags: { priority: "high" },
            });
            return false;
        }
        const response = await fetchJson("/llm-models", options);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in fetchLlmModels",
                },
                tags: {
                    priority: "high",
                },
            });
            return false;
        }
        setLlmModels(response.data);
    }
    useEffect(() => {
        const fetchData = async () => {
            await fetchCompanies();
            await fetchLlmModels();
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(_Fragment, { children: [viewingSteps && (_jsx(Steps, { steps: steps, onAddStep: () => {
                    setViewingSteps(false);
                    setViewingForm(true);
                    setViewingWatchlist(false);
                }, deleteStep: deleteStep, onRunUpdate: runUpdate })), viewingForm && (_jsx(UpdaterForm, { onSubmitStep: addStep, hasPreviousSteps: steps.length > 0, defaultValues: steps.at(-1)
                    ? { ...steps.at(-1), source_urls: [] }
                    : undefined, defaultMultiSelectOptions: multiSelectOptions, onRunUpdate: runUpdate, tickers: tickers, llmModels: llmModels })), viewingWatchlist && (_jsx(WatchList, { companies: companies, onBackButtonClick: returnToWorkflow })), _jsx(Footer, { afterSignOutUrl: `${CLIENT_URL}/index.html`, manageWatchlist: manageWatchlist, watchlistEnabled: !updateIsRunning })] }));
};
Office.onReady(() => {
    const rootElement = document.getElementById("app-root");
    if (!rootElement) {
        captureErrorWithContext(new Error("Root element #app-root not found"));
        throw new Error("Root element not found");
    }
    const root = createRoot(rootElement, {
        onUncaughtError: (error) => captureErrorWithContext(error),
        onRecoverableError: (error) => captureErrorWithContext(error),
    });
    root.render(_jsx(StrictMode, { children: _jsx(Sentry.ErrorBoundary, { fallback: FallbackComponent, showDialog: true, children: _jsxs(ClerkProvider, { publishableKey: PUBLISHABLE_KEY, children: [_jsx(SignedOut, { children: _jsx(SignInPage, { redirect_url: `${CLIENT_URL}/index.html` }) }), _jsxs(SignedIn, { children: [_jsx(SentryUserSync, {}), _jsx(App, {})] })] }) }) }));
});
